import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Shield,
  ShieldCheck,
  ShieldOff,
  Search,
  Loader2,
  CheckCircle2,
  XCircle,
  Users
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HRERPAccess = () => {
  const { getAuthHeader } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [updating, setUpdating] = useState({});

  const fetchEmployees = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/hr/employees`, {
        headers: getAuthHeader()
      });
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const toggleERPAccess = async (employeeId, currentAccess) => {
    setUpdating(prev => ({ ...prev, [employeeId]: true }));
    
    try {
      await axios.put(
        `${BACKEND_URL}/api/hr/employees/${employeeId}`,
        {
          erp_access: !currentAccess,
          erp_role: !currentAccess ? 'viewer' : null
        },
        { headers: getAuthHeader() }
      );
      
      // Update local state
      setEmployees(employees.map(emp => 
        emp.employee_id === employeeId 
          ? { ...emp, erp_access: !currentAccess, erp_role: !currentAccess ? 'viewer' : null }
          : emp
      ));
    } catch (error) {
      console.error('Error updating ERP access:', error);
      alert('Failed to update ERP access');
    } finally {
      setUpdating(prev => ({ ...prev, [employeeId]: false }));
    }
  };

  const updateERPRole = async (employeeId, newRole) => {
    setUpdating(prev => ({ ...prev, [employeeId]: true }));
    
    try {
      await axios.put(
        `${BACKEND_URL}/api/hr/employees/${employeeId}`,
        { erp_role: newRole },
        { headers: getAuthHeader() }
      );
      
      // Update local state
      setEmployees(employees.map(emp => 
        emp.employee_id === employeeId ? { ...emp, erp_role: newRole } : emp
      ));
    } catch (error) {
      console.error('Error updating ERP role:', error);
      alert('Failed to update ERP role');
    } finally {
      setUpdating(prev => ({ ...prev, [employeeId]: false }));
    }
  };

  const filteredEmployees = employees.filter(emp =>
    emp.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.employee_id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const erpRoles = [
    { value: 'project_manager', label: 'Project Manager', color: 'text-blue-700 bg-blue-100' },
    { value: 'site_engineer', label: 'Site Engineer', color: 'text-orange-700 bg-orange-100' },
    { value: 'finance_manager', label: 'Finance Manager', color: 'text-green-700 bg-green-100' },
    { value: 'procurement_officer', label: 'Procurement Officer', color: 'text-purple-700 bg-purple-100' },
    { value: 'viewer', label: 'Viewer (Read Only)', color: 'text-gray-700 bg-gray-100' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const employeesWithAccess = employees.filter(emp => emp.erp_access).length;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">ERP Access Management</h1>
        <p className="text-gray-600 mt-2">
          Grant or revoke Florentix ERP access for employees
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Employees</p>
                <p className="text-2xl font-bold text-gray-900">{employees.length}</p>
              </div>
              <Users className="w-10 h-10 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">ERP Access Granted</p>
                <p className="text-2xl font-bold text-green-600">{employeesWithAccess}</p>
              </div>
              <ShieldCheck className="w-10 h-10 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">No ERP Access</p>
                <p className="text-2xl font-bold text-gray-600">
                  {employees.length - employeesWithAccess}
                </p>
              </div>
              <ShieldOff className="w-10 h-10 text-gray-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search employees..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
      </div>

      {/* Info Banner */}
      <Card className="mb-6 bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">ERP Roles Explained:</p>
              <ul className="space-y-1 list-disc list-inside">
                <li><strong>Project Manager:</strong> Full project access, can create/edit projects and tasks</li>
                <li><strong>Site Engineer:</strong> Site management, progress tracking, issue reporting</li>
                <li><strong>Finance Manager:</strong> Budgets, invoices, payments, financial reports</li>
                <li><strong>Procurement Officer:</strong> Material orders, vendor management, inventory</li>
                <li><strong>Viewer:</strong> Read-only access to assigned modules</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Employees List */}
      <Card>
        <CardHeader>
          <CardTitle>Employee List</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {filteredEmployees.map((employee) => (
              <div
                key={employee.employee_id}
                className="flex flex-col md:flex-row md:items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-gray-900">{employee.full_name}</h3>
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                      {employee.employee_id}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">{employee.email}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {employee.designation} • {employee.department}
                  </p>
                </div>

                <div className="flex items-center gap-3 mt-4 md:mt-0">
                  {/* ERP Role Selector */}
                  {employee.erp_access && (
                    <select
                      value={employee.erp_role || 'viewer'}
                      onChange={(e) => updateERPRole(employee.employee_id, e.target.value)}
                      disabled={updating[employee.employee_id]}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      {erpRoles.map(role => (
                        <option key={role.value} value={role.value}>
                          {role.label}
                        </option>
                      ))}
                    </select>
                  )}

                  {/* Toggle Access Button */}
                  <Button
                    onClick={() => toggleERPAccess(employee.employee_id, employee.erp_access)}
                    disabled={updating[employee.employee_id]}
                    className={`min-w-[140px] ${
                      employee.erp_access
                        ? 'bg-red-600 hover:bg-red-700 text-white'
                        : 'bg-green-600 hover:bg-green-700 text-white'
                    }`}
                  >
                    {updating[employee.employee_id] ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : employee.erp_access ? (
                      <XCircle className="w-4 h-4 mr-2" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                    )}
                    {employee.erp_access ? 'Revoke Access' : 'Grant Access'}
                  </Button>
                </div>
              </div>
            ))}

            {filteredEmployees.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No employees found matching your search.
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HRERPAccess;
